
import { extractAndInsertChart } from "@/helpers/chart-extraction-handlers";
import { logger } from "@/helpers/tslog";

const getUserId = async (): Promise<string> => {
  try {
    const { getToken } = await import("@/utils/localstorage");
    const token = await getToken();
    if (!token) {
      throw new Error("No authentication token found");
    }

    const apiUrl = process.env.API_URL as string;
    const response = await fetch(`${apiUrl}/user/my-user-id`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get userId: ${response.status}`);
    }

    const data = await response.json();
    return data.userId;
  } catch (error) {
    logger.warn("Failed to get userId from server:", error);
    // Fallback: generate a session-based ID
    return `ppt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};

import { selectionChatImage } from "@/utils/placeholderSelection";
import Control = Office.Control;
import Group = Office.Group;
import Tab = Office.Tab;
import RibbonUpdaterData = Office.RibbonUpdaterData;
import { fetchChartDocuments } from "@/utils/fetchChartDocuments";
import { updateButtonStatus } from "@/utils/contentReady/helper";
import { extractAndGetSlidesImages } from "@/utils/formatTemplate/slides-extraction-handlers";
import { openErrorWindow } from "@/utils/error";

import {
  alphaEditPopupOpen,
  alphaPopupOpen,
  closeLoadingPopup,
  feedbackSupportPopup,
  formatTemplatePopup,
  openLoadingPopup,
  openPopup,
  openContentManager,
  placeholderPopup,
  presentationPopup,
  slidesPopup,
  slideEditPopup,
} from "@/utils/popupDialogManager";
import { setPowerpointIdLocal } from "@/utils/contentmanager/powerpointId";
import { fetchDocumentById, updateDocumentPlaceholder } from "@/utils/contentmanager/documents";

Office.onReady(async () => {
  Office.addin
    .setStartupBehavior(Office.StartupBehavior.load)
    .then(async () => {
      logger.info("[PowerPoint] Startup behavior set to load");
      try {
        const current = await Office.addin.getStartupBehavior();
        logger.info("[PowerPoint] Verified StartupBehavior:", current);
      } catch {}
    })
    .catch((err) => {
      logger.error("[PowerPoint] Failed to set startup behavior", err);
    });
  let uuid = Office.context.document.settings.get("ALPHASCRIPT");
  logger.info("Setting UUID:", uuid);
  if (!uuid) {
    uuid = `AlphaScript-${crypto.randomUUID()}`;
    Office.context.document.settings.set("ALPHASCRIPT", uuid);
    Office.context.document.settings.saveAsync();
  }
  setPowerpointIdLocal(uuid);
  // Auto-open taskpane once per document session, then hide
  try {
    const sessionKey = `tpOnce-${uuid}`;
    if (!sessionStorage.getItem(sessionKey)) {
      sessionStorage.setItem(sessionKey, "1");
      await Office.addin
        .showAsTaskpane()
        .then(async () => {
          // Give it a tick to initialize, then hide
          await new Promise((r) => setTimeout(r, 150));
          try {
            await Office.addin.hide();
          } catch {}
        })
        .catch(() => {});
    }
  } catch {}
  // If needed, Office.js is ready to be called.
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
    await selectionChatImage();
  });
  const charts = await fetchChartDocuments(uuid);
  if (charts.length > 0) {
    await updateButtonStatus(true);
  } else {
    await updateButtonStatus(false);
  }

  // Persistent Socket.IO connection on PowerPoint open for test relay
  try {
    const { default: io } = await import("socket.io-client");
    const apiUrl = process.env.API_URL as string | undefined;
    if (apiUrl) {
      logger.info("[PowerPoint] Attempting Socket.IO connect to:", apiUrl);
      const previousSession = (() => {
        try {
          return localStorage.getItem("pptSocketSession") || undefined;
        } catch {
          return undefined;
        }
      })();
      const socket = io(apiUrl, {
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        auth: previousSession ? { session: previousSession } : undefined,
      } as any);
      // Expose socket for command handlers
      (window as any).__pptSocket = socket;
      let isSocketConnected = false;
      let selectionHandlerAdded = false;


      const saveConnectedWorkbook = (payload: any) => {
        try {
          const existingRaw = localStorage.getItem("connectedExcelWorkbooks");
          let existingWorkbooks = existingRaw ? JSON.parse(existingRaw) : [];

          if (!Array.isArray(existingWorkbooks)) {
            existingWorkbooks = [];
          }

          // Check if workbook already exists
          const existingIndex = existingWorkbooks.findIndex((wb) => wb.workbookGuid === payload?.workbookGuid);

          if (existingIndex !== -1) {
            // Update existing workbook
            existingWorkbooks[existingIndex] = {
              workbookGuid: payload?.workbookGuid,
              linkIds: payload?.linkIds || [],
              hasLinks: !!payload?.hasLinks,
              connectedAt: payload?.timestamp,
              lastSeen: new Date().toISOString(),
            };
          } else {
            // Add new workbook
            existingWorkbooks.push({
              workbookGuid: payload?.workbookGuid,
              linkIds: payload?.linkIds || [],
              hasLinks: !!payload?.hasLinks,
              connectedAt: payload?.timestamp,
              lastSeen: new Date().toISOString(),
            });
          }

          localStorage.setItem("connectedExcelWorkbooks", JSON.stringify(existingWorkbooks));
        } catch (_) {}
      };

      const clearConnectedWorkbook = (workbookGuid?: string) => {
        try {
          if (workbookGuid) {
            // Remove specific workbook
            const existingRaw = localStorage.getItem("connectedExcelWorkbooks");
            if (existingRaw) {
              let existingWorkbooks = JSON.parse(existingRaw);
              if (Array.isArray(existingWorkbooks)) {
                existingWorkbooks = existingWorkbooks.filter((wb) => wb.workbookGuid !== workbookGuid);
                localStorage.setItem("connectedExcelWorkbooks", JSON.stringify(existingWorkbooks));
              }
            }
          } else {
            // Clear all workbooks
            localStorage.removeItem("connectedExcelWorkbooks");
          }
        } catch (_) {}
      };

      // On selection change, reevaluate whether the selected shape is a chart linked
      // to a currently connected Excel workbook, and enable/disable the Refresh button accordingly.
      const handleSelectionChanged = async () => {
        try {
          if (!isSocketConnected) {
            logger.info("[PowerPoint] Selection changed, but socket not connected → disabling Refresh");
            // await setRefreshButtonEnabled(false);
            return;
          }
          // Reuse the tag matching logic against stored workbook data
          // await evaluateRefreshEligibilityFromSelection();  
        } catch (_) {
          // await setRefreshButtonEnabled(false);
        }
      };

      socket.on("connect", () => {
        logger.info("[PowerPoint] Socket connected:", socket.id);
        isSocketConnected = true;
        // Persist session id for resume attempts
        try {
          localStorage.setItem("pptSocketSession", socket.id);
        } catch {}
        // Optional: notify server we are resuming (if supported)
        try {
          const session = localStorage.getItem("pptSocketSession");
          if (session) {
            socket.emit("pptResume", { session });
          }
        } catch {}
        // Get userId from server and notify server
        getUserId().then(userId => {
          socket.emit("pptConnected", { clientType: "powerpoint", userId });
        });
        
        // if (!selectionHandlerAdded) {
        //   Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
        //     await handleSelectionChanged();
        //   });
        //   selectionHandlerAdded = true;
        // }
        // // Evaluate current selection immediately on connect
        // void handleSelectionChanged();
      });

      socket.on("reconnect", () => {
        logger.info("[PowerPoint] Socket reconnected");
        isSocketConnected = true;
        try {
          localStorage.setItem("pptSocketSession", socket.id);
        } catch {}
        
        // Get userId from server and notify server
        getUserId().then(userId => {
          socket.emit("pptConnected", { clientType: "powerpoint", userId });
        });
        
        // void handleSelectionChanged();
      });

      // Handle initial workbook data when PowerPoint connects
      socket.on("workbookData", async (payload: any) => {
        logger.info("[PowerPoint] workbookData event received (initial data)", payload);
        try {
          if (payload?.workbooks && Array.isArray(payload.workbooks)) {
            // Save all currently open workbooks
            for (const workbook of payload.workbooks) {
              saveConnectedWorkbook(workbook);
            }
            
            // Evaluate against the currently selected shape's AlphaScript tags immediately
            logger.debug("[PowerPoint] Evaluating selection after workbookData receipt");
            // await evaluateRefreshEligibilityFromSelection();

            // Optional toast for visibility
            try {
              // eslint-disable-next-line no-alert
              alert(
                `Found ${payload.workbooks.length} open Excel workbook(s)` +
                  `\nTotal links available: ${payload.workbooks.reduce((sum, wb) => sum + (Array.isArray(wb?.linkIds) ? wb.linkIds.length : 0), 0)}`
              );
            } catch (_) {}
          }
        } catch (error) {
          logger.error("[PowerPoint] Error processing workbookData payload:", error);
        }
      });

      socket.on("workbookConnected", async (payload: any) => {
        logger.info("[PowerPoint] workbookConnected event received (startup listener)", payload);
        try {
          // Persist the connected workbook info for later selection checks
          saveConnectedWorkbook(payload);

          // Evaluate against the currently selected shape's AlphaScript tags immediately
          logger.debug("[PowerPoint] Evaluating selection after workbookConnected receipt");
          // await evaluateRefreshEligibilityFromSelection();

          // Optional toast for visibility
          try {
            // eslint-disable-next-line no-alert
            alert(
              `Excel workbook connected: ${payload?.workbookGuid}` +
                `\nLinks available: ${Array.isArray(payload?.linkIds) ? payload.linkIds.length : 0}`
            );
          } catch (_) {}
        } catch (error) {
          logger.error("[PowerPoint] Error processing workbookConnected payload:", error);
        }
      });

      socket.on("connect_error", (err) => {
        logger.warn("[PowerPoint] Socket connect_error:", err?.message || err);
        isSocketConnected = false;
        clearConnectedWorkbook();
        // void setRefreshButtonEnabled(false);
      });

      socket.on("disconnect", (reason) => {
        logger.info("[PowerPoint] Socket disconnected:", reason);
        isSocketConnected = false;
        try {
          localStorage.removeItem("connectedExcelWorkbooks");
        } catch {}
        // void setRefreshButtonEnabled(false);
      });

      // Excel explicitly notified it's unloading/closing
      socket.on("workbookDisconnected", async (payload: any) => {
        try {
          logger.info("[PowerPoint] workbookDisconnected received", payload);
          // Remove specific workbook if payload contains workbookGuid
          if (payload?.workbookGuid) {
            clearConnectedWorkbook(payload.workbookGuid);
          }
        } catch {}
        // await setRefreshButtonEnabled(false);
      });

      // Receive refreshed chart content from Excel and replace in the selected shape (single insert)
      let __pptReplacing = false;
      socket.on("pptRefreshResponse", async (payload: any) => {
        if (__pptReplacing) {
          return;
        }
        __pptReplacing = true;
        try {
          // Insert starting: close loading popup and re-enable the Refresh button
          closeLoadingPopup();
          await setRefreshButtonEnabled(true);
          logger.info("[PowerPoint] pptRefreshResponse received");
          await PowerPoint.run(async (context) => {
            let selectedShapes = context.presentation.getSelectedShapes();
           
            if(selectedShapes){
              selectedShapes.load("items");
            await context.sync();
            }
           
            if (!selectedShapes.items || selectedShapes.items.length === 0) {
              logger.warn("[PowerPoint] No selected shape to insert refreshed chart");
              const placeholder = JSON.parse(localStorage.getItem("placeholderChartRefreshCM") || "{}");
              if (placeholder) {
                const slide = context.presentation.slides.getItem(placeholder.slideId);
                if (slide) {
                  slide.setSelectedShapes([placeholder.shapeId]);
                  await context.sync();
                }
              }
              selectedShapes = context.presentation.getSelectedShapes();
              selectedShapes.load("items");
              await context.sync();
            }
            const shape = selectedShapes.items[0];
            shape.load(["id", "left", "top", "width", "height", "tags"]);
            const slide = shape.getParentSlide();
            slide.load(["id"]);
            await context.sync();

            // Capture existing tags to reapply after replacement
            const tagMap: Record<string, string> = {};
            const tags = shape.tags;
            if (tags) {
              tags.load("items");
              await context.sync();
              for (const t of tags.items as any[]) {
                (t as any).load(["key", "value"]);
              }
              await context.sync();
              for (const t of tags.items as any[]) {
                const k = (t as any).key as string;
                const v = (t as any).value as string;
                if (k && v) tagMap[k.toUpperCase()] = v;
              }
            }

            const left = Number(shape.left) || 0;
            const top = Number(shape.top) || 0;
            const width = Number(shape.width) || 0;
            const height = Number(shape.height) || 0;
            const slideId = slide.id;

            // Delete old shape
            shape.delete();
            await context.sync();

            // Ensure the target slide is selected
            context.presentation.setSelectedSlides([slideId]);
            await context.sync();

            const options: Office.SetSelectedDataOptions = {
              coercionType: Office.CoercionType.Image,
              imageLeft: left,
              imageTop: top,
              imageWidth: width,
              imageHeight: height,
            } as any;

            // Prepare base64 image content from payload
            let imageBase64: string = "";
            if (typeof payload?.content === "string") {
              if (payload?.contentType?.includes("image/") || payload?.contentEncoding === "base64") {
                imageBase64 = payload.content;
              }
            }

            if (!imageBase64) {
              logger.warn("[PowerPoint] Empty or unsupported image content for chart refresh");
              openErrorWindow("chart-not-correctly-refresh");
              return;
            }

            logger.info("[PowerPoint] Preparing to insert refreshed image");

            await new Promise<void>((resolve) => {
              Office.context.document.setSelectedDataAsync(imageBase64, options, async (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                  logger.warn("[PowerPoint] Failed to insert refreshed image:", result.error?.message);
                  return resolve();
                }
                logger.info("[PowerPoint] Refreshed image inserted successfully");

                // Re-tag the newly inserted shape (best-effort: pick the largest shape near the same bounds)
                try {
                  const slides = context.presentation.slides;
                  slides.load("items");
                  await context.sync();
                  const tgtSlide = slides.getItem(slideId);
                  const shapes = tgtSlide.shapes;
                  shapes.load("items/$none");
                  await context.sync();
                  let best: any = null;
                  let minDist = Number.MAX_VALUE;
                  for (const s of shapes.items) {
                    s.load(["id", "left", "top", "width", "height"]);
                    await context.sync();
                    const dx = Number(s.left) - left;
                    const dy = Number(s.top) - top;
                    const dw = Number(s.width) - width;
                    const dh = Number(s.height) - height;
                    const dist = Math.abs(dx) + Math.abs(dy) + Math.abs(dw) + Math.abs(dh);
                    if (dist < minDist) {
                      minDist = dist;
                      best = s;
                    }
                  }
                  if (best) {
                    if (tagMap["WORKBOOKGUID"]) best.tags.add("WORKBOOKGUID", tagMap["WORKBOOKGUID"]);
                    if (tagMap["LINKID"]) best.tags.add("LINKID", tagMap["LINKID"]);
                    if (tagMap["CHARTNAME"]) best.tags.add("CHARTNAME", tagMap["CHARTNAME"]);
                    if (tagMap["RENDER"]) best.tags.add("RENDER", tagMap["RENDER"]);
                    if (tagMap["GENERATED"]) best.tags.add("GENERATED", tagMap["GENERATED"]);
                    if (tagMap["DOCUMENTID"]) best.tags.add("DOCUMENTID", tagMap["DOCUMENTID"]);
                    if (tagMap["EXCELFILEURL"]) best.tags.add("EXCELFILEURL", tagMap["EXCELFILEURL"]);
                    await context.sync();
                  }
                  best.load(["id", "left", "top", "width", "height", "slideId", "slideIndex", "name", "type"]);
                  await context.sync();
                  const newPlaceholder = {
                    slideId: slideId,
                    shapeId: best.id,
                    slideIndex: slide.id,
                    name: best.name,
                    shapeType: best.type,
                    height: best.height,
                    width: best.width,
                    left: best.left,
                    top: best.top,
                  };

                  const documentId = tagMap["DOCUMENTID"];
                  const documentData = await fetchDocumentById(documentId);
                  const documentContent = documentData?.documentContent;
                  const documentDataToSave = JSON.parse(documentContent);
                  const updatedDocumentContent = {
                    ...documentDataToSave,
                    placeholder: {
                      slideNumber: documentDataToSave.placeholder.slideNumber,
                      ...newPlaceholder,
                    },
                  };

                  await updateDocumentPlaceholder(documentId, documentData, updatedDocumentContent);
                } catch (e) {
                  logger.warn("[PowerPoint] Failed to re-tag inserted shape", e as any);
                }
                resolve();
              });
            });
          });
          let p = { type: "CONTENT_MANAGER_REFRESH", message: "Chart refreshed successfully." };
          localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(p));
        } catch (e) {
          logger.warn("[PowerPoint] Failed handling pptRefreshResponse", e as any);
          // closeLoadingPopup();
          // await setRefreshButtonEnabled(true);
          localStorage.removeItem("placeholderChartRefreshCM");
          let p = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Failed to refresh chart." };
          localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(p));
          openErrorWindow("refresh-chart");
        } finally {
          __pptReplacing = false;
        }
      });
    }
  } catch (e) {
    logger.warn("[PowerPoint] startup socket setup failed:", e);
  }
});

// Re-evaluate Refresh eligibility using the current selection and latest socket payload
async function evaluateRefreshEligibilityFromSelection(_payload?: any) {
  try {
    await PowerPoint.run(async (context) => {
      const selectedShapes = context.presentation.getSelectedShapes();
      selectedShapes.load("items");
      await context.sync();

      if (selectedShapes.items.length === 0) {
        // await setRefreshButtonEnabled(false);
        return;
      }

      const shape = selectedShapes.items[0];
      shape.load(["tags"]);
      await context.sync();

      const tagsCollection = shape.tags;
      if (!tagsCollection) {
        // await setRefreshButtonEnabled(false);
        return;
      }

      tagsCollection.load("items");
      await context.sync();

      const allTags = tagsCollection.items || [];
      for (let i = 0; i < allTags.length; i++) {
        allTags[i].load("key,value");
      }
      await context.sync();

      const alphaScriptTags: Record<string, string> = {};
      for (let i = 0; i < allTags.length; i++) {
        const tag: any = allTags[i];
        if (tag?.key && tag?.value) {
          alphaScriptTags[tag.key] = tag.value;
        }
      }

      const connectedRaw = localStorage.getItem("connectedExcelWorkbooks");
      if (!connectedRaw) {
        // await setRefreshButtonEnabled(false);
        return;
      }

      const connectedWorkbooks = JSON.parse(connectedRaw) as Array<{ workbookGuid?: string; linkIds?: string[] }>;
      if (!Array.isArray(connectedWorkbooks)) {
        // await setRefreshButtonEnabled(false);
        return;
      }

      const tagWorkbook = alphaScriptTags["WORKBOOKGUID"];
      const tagLinkId = alphaScriptTags["LINKID"];

      if (!tagWorkbook || !tagLinkId) {
        // await setRefreshButtonEnabled(false);
        return;
      }

      // Check if any connected workbook matches the shape's tags
      const matchingWorkbook = connectedWorkbooks.find(
        (wb) => wb.workbookGuid === tagWorkbook && Array.isArray(wb.linkIds) && wb.linkIds.includes(tagLinkId)
      );

      // await setRefreshButtonEnabled(!!matchingWorkbook);
    });
  } catch (_) {
    // await setRefreshButtonEnabled(false);
  }
}

/**
 * Shows a notification when the add-in command is executed.
 * @param event
 */
function action(event: Office.AddinCommands.Event) {
  const message: Office.NotificationMessageDetails = {
    type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
    message: "Performed action.",
    icon: "Icon.80x80",
    persistent: true,
  };

  // Show a notification message.
  Office.context.mailbox.item?.notificationMessages.replaceAsync("ActionPerformanceNotification", message);

  // Be sure to indicate when the add-in command function is complete.
  event.completed();
}

Office.actions.associate("action", action);

export async function insertChartImage(event?: Office.AddinCommands.Event) {
  try {
    await extractAndInsertChart(event, () => {
      logger.info("Placeholder obtained");
    });
  } catch (error) {
    logger.error("Error in insertChartImage:", error);
    event?.completed();
  }
}

export async function extractSlideImages(event?: Office.AddinCommands.Event) {
  try {
    await extractAndGetSlidesImages(event);
  } catch (error) {
    logger.error("Error in insertChartImage:", error);
    event?.completed();
  }
}

// Enable/disable only the Refresh ribbon button
export const setRefreshButtonEnabled = async (enabled: boolean) => {
  const button: Control = { id: "RefreshButton", enabled };
  const parentGroup: Group = { id: "manageContent", controls: [button] };
  const parentTab: Tab = { id: "AlphaScriptTab", groups: [parentGroup] };
  const ribbonUpdater: RibbonUpdaterData = { tabs: [parentTab] };

  try {
    await Office.ribbon.requestUpdate(ribbonUpdater);
    logger.info(`Refresh button ${enabled ? "enabled" : "disabled"} successfully.`);
  } catch (error) {
    logger.info("Error updating Refresh button state:", error as any);
    if ((error as any).code === "HostRestartNeeded") {
      logger.info("Host restart required. Please save your work, close, and restart Office.");
    } else {
      throw error;
    }
  }
};

export function openContentReadyTaskpane() {
  Office.addin
    .showAsTaskpane()
    .then(() => {
      logger.info("Taskpane open successfully");
      try {
        (window as any).postMessage({ type: "REFRESH_CHARTS" }, "*");
      } catch {}
    })
    .catch((error) => {
      logger.error("Failed to show as taskpane:", error);
    });
}

Office.actions.associate("openPopup", openPopup);
// Office.actions.associate("insertChartImage", insertChartImage);

export const enableButton = async (value: boolean) => {
  const button: Control = { id: "AlphaEditButton", enabled: value };
  const parentGroup: Group = { id: "AlphaScriptGroup", controls: [button] };
  const parentTab: Tab = { id: "AlphaScriptTab", groups: [parentGroup] };
  const ribbonUpdater: RibbonUpdaterData = { tabs: [parentTab] };

  try {
    await Office.ribbon.requestUpdate(ribbonUpdater);
    logger.info("Ribbon button enabled successfully.");
  } catch (error) {
    logger.info("Error updating ribbon:", error);
    // Handle known error HostRestartNeeded or rethrow if necessary
    if (error.code === "HostRestartNeeded") {
      logger.info("Host restart required. Please save your work, close, and restart Office.");
      // Optionally notify user here
    } else {
      throw error;
    }
  }
};

Office.actions.associate("insertChartImage", alphaPopupOpen);
Office.actions.associate("formatTemplatePopup", formatTemplatePopup);
Office.actions.associate("feedbackSupportPopup", feedbackSupportPopup);
Office.actions.associate("alphaCreatePopup", alphaPopupOpen);
Office.actions.associate("alphaEditPopup", alphaEditPopupOpen);
Office.actions.associate("openContentManager", openContentManager);
Office.actions.associate("slidesPopup", slidesPopup);
Office.actions.associate("slideEditPopup", slideEditPopup);
Office.actions.associate("presentationPopup", presentationPopup);
// Delay the placeholder popup slightly to allow taskpane resources to initialize
const placeholderPopupDelayed = async (event: Office.AddinCommands.Event) => {
  try {
    const onceKey = 'placeholderPopupOnce';
    const hasRun = sessionStorage.getItem(onceKey);
    if (!hasRun) {
      sessionStorage.setItem(onceKey, '1');
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  } catch {}
  return placeholderPopup(event);
};

Office.actions.associate("placeholderPopup", placeholderPopupDelayed);

export async function refreshButtonClick(event?: Office.AddinCommands.Event) {
  try {
    // First-click guidance: show info one-time per user (persisted in localStorage with userId)
    try {
      const onceBaseKey = 'refreshInfoOnce';
      const userId = await getUserId();
      const infoOnceKey = `${onceBaseKey}:${userId || 'anon'}`;
      if (!localStorage.getItem(infoOnceKey)) {
        localStorage.setItem(infoOnceKey, '1');
        openErrorWindow('refresh-info');
        event?.completed();
        return;
      }
    } catch {}
    const socket: any = (window as any).__pptSocket;
    if (!socket || socket.disconnected) {
      logger.warn("[PowerPoint] Refresh click ignored: socket not connected");
      event?.completed();
      return;
    }
    PowerPoint.run(async (context) => {
      const selectedShapes = context.presentation.getSelectedShapes();
      selectedShapes.load("items");
      await context.sync();
      if (!selectedShapes.items || selectedShapes.items.length === 0) {
        // If no image is selected, show explicit guidance and info
        logger.warn("[PowerPoint] No shape selected for refresh request");
        openErrorWindow('chart-not-selected');
        event?.completed();
        return;
      }
      const shape = selectedShapes.items[0];
      shape.load(["tags", "name", "id"]);
      await context.sync();
      const tags = shape.tags;
      if (!tags) {
        // Selected shape is not a tagged AlphaScript chart; instruct user to select the correct chart
        logger.warn("[PowerPoint] Selected shape has no tags");
        openErrorWindow('chart-not-selected');
        event?.completed();
        return;
      }
      tags.load("items");
      await context.sync();
      const tagMap: Record<string, string> = {};
      for (const t of tags.items as any[]) {
        (t as any).load(["key", "value"]);
      }
      await context.sync();
      for (const t of tags.items as any[]) {
        const k = (t as any).key as string;
        const v = (t as any).value as string;
        if (k && v) tagMap[k.toUpperCase()] = v;
      }
      const workbookGuid = tagMap["WORKBOOKGUID"];
      const linkId = tagMap["LINKID"];
      if (!workbookGuid || !linkId) {
        // Selected shape is missing required chart linkage; prompt the user to select the chart again
        logger.warn("[PowerPoint] Missing WORKBOOKGUID/LINKID on selected shape");
        openErrorWindow('chart-not-selected');
        event?.completed();
        return;
      }

      // Verify the workbook-link combination is still valid and update last seen
      const connectedRaw = localStorage.getItem("connectedExcelWorkbooks");
      if (!connectedRaw) {
        logger.warn("[PowerPoint] No connected Excel workbooks info found");
        openErrorWindow('workbook-not-open');
        event?.completed();
        return;
      }
      const connectedWorkbooks = JSON.parse(connectedRaw) as Array<{ workbookGuid?: string; linkIds?: string[] }>;
      if (Array.isArray(connectedWorkbooks)) {
          const matchingWorkbook = connectedWorkbooks.find(
            (wb) => wb.workbookGuid === workbookGuid && Array.isArray(wb.linkIds) && wb.linkIds.includes(linkId)
          );

        if (matchingWorkbook) {
          const userId = await getUserId();

          const payload = {
            workbookGuid,
            linkId,
            slideShape: { id: shape.id, name: shape.name },
            requestedAt: new Date().toISOString(),
            userId
          };
          logger.info("[PowerPoint] Emitting pptRefreshRequest", payload);
          try {
            socket.emit("pptRefreshRequest", payload);
            openLoadingPopup();
            await setRefreshButtonEnabled(false);
          } catch (e) {
            logger.warn("emit pptRefreshRequest failed", e as any);
            openErrorWindow("refresh-chart");
          }
          event?.completed();
        }
      } else {
        logger.warn("[PowerPoint] connectedExcelWorkbooks is not an array");
        openErrorWindow('workbook-not-open');
        event?.completed();
        return;
      }
    });
  } catch (e) {
    logger.error("[PowerPoint] refreshButtonClick failed", e as any);
    openErrorWindow("refresh-chart");
    event?.completed();
  }
}


Office.actions.associate("refreshButtonClick", refreshButtonClick);


export async function ExcelOpenButtonClick(event?: Office.AddinCommands.Event) {
  try {
   
    PowerPoint.run(async (context) => {
      const selectedShapes = context.presentation.getSelectedShapes();
      selectedShapes.load("items");
      await context.sync();
      if (!selectedShapes.items || selectedShapes.items.length === 0) {
        // If no image is selected, show explicit guidance and info
        logger.warn("[PowerPoint] No shape selected for refresh request");
        openErrorWindow('chart-not-selected');
        event?.completed();
        return;
      }
      const shape = selectedShapes.items[0];
      shape.load(["tags", "name", "id"]);
      await context.sync();
      const tags = shape.tags;
      if (!tags) {
        // Selected shape is not a tagged AlphaScript chart; instruct user to select the correct chart
        logger.warn("[PowerPoint] Selected shape has no tags");
        openErrorWindow('chart-not-selected');
        event?.completed();
        return;
      }
      tags.load("items");
      await context.sync();
      const tagMap: Record<string, string> = {};
      for (const t of tags.items as any[]) {
        (t as any).load(["key", "value"]);
      }
      await context.sync();
      for (const t of tags.items as any[]) {
        const k = (t as any).key as string;
        const v = (t as any).value as string;
        if (k && v) tagMap[k.toUpperCase()] = v;
      }

      const excelFileUrl = tagMap["EXCELFILEURL"];
      if (excelFileUrl) {
        // Selected shape is missing required chart linkage; prompt the user to select the chart again
        window.open(excelFileUrl, "_blank");
        event?.completed();
        return;
      } else {
        logger.warn("[PowerPoint] Missing EXCELFILEURL on selected shape");
        openErrorWindow('excel-file-url-not-found');
        event?.completed();
        return;
      }

      
      
    });
  } catch (e) {
    logger.error("[PowerPoint] refreshButtonClick failed", e as any);
    openErrorWindow("excel-file-url-not-found");
    event?.completed();
  }
}


// Function to show current workbook status (for debugging/testing)
export function showWorkbookStatus(event?: Office.AddinCommands.Event) {
  try {
    const connectedRaw = localStorage.getItem("connectedExcelWorkbooks");
    if (!connectedRaw) {
      alert("No workbooks connected");
      event?.completed();
      return;
    }

    const connectedWorkbooks = JSON.parse(connectedRaw) as Array<{
      workbookGuid?: string;
      linkIds?: string[];
      hasLinks?: boolean;
      connectedAt?: string;
      lastSeen?: string;
    }>;

    if (!Array.isArray(connectedWorkbooks) || connectedWorkbooks.length === 0) {
      alert("No workbooks connected");
      event?.completed();
      return;
    }

    let message = `Connected Workbooks (${connectedWorkbooks.length}):\n\n`;

    connectedWorkbooks.forEach((wb, index) => {
      message += `${index + 1}. Workbook: ${wb.workbookGuid}\n`;
      message += `   Links: ${wb.linkIds?.length || 0}\n`;
      message += `   Has Links: ${wb.hasLinks ? "Yes" : "No"}\n`;
      message += `   Connected: ${wb.connectedAt ? new Date(wb.connectedAt).toLocaleTimeString() : "Unknown"}\n`;
      message += `   Last Seen: ${wb.lastSeen ? new Date(wb.lastSeen).toLocaleTimeString() : "Unknown"}\n\n`;
    });

    alert(message);
    event?.completed();
  } catch (error) {
    logger.error("[PowerPoint] Failed to show workbook status", error);
    alert("Error showing workbook status");
    event?.completed();
  }
}

// Function to get current workbook status
export function getWorkbookStatus(): any {
  try {
    const connectedRaw = localStorage.getItem("connectedExcelWorkbooks");
    if (!connectedRaw) {
      return { total: 0, workbooks: [] };
    }

    const connectedWorkbooks = JSON.parse(connectedRaw) as Array<{
      workbookGuid?: string;
      linkIds?: string[];
      hasLinks?: boolean;
      connectedAt?: string;
      lastSeen?: string;
    }>;

    if (!Array.isArray(connectedWorkbooks)) {
      return { total: 0, workbooks: [] };
    }

    const withLinks = connectedWorkbooks.filter((wb) => wb.hasLinks).length;
    const totalLinks = connectedWorkbooks.reduce((sum, wb) => sum + (wb.linkIds?.length || 0), 0);

    return {
      total: connectedWorkbooks.length,
      withLinks,
      totalLinks,
      workbooks: connectedWorkbooks.map((wb) => ({
        workbookGuid: wb.workbookGuid,
        linkIdsCount: wb.linkIds?.length || 0,
        hasLinks: wb.hasLinks,
        connectedAt: wb.connectedAt,
        lastSeen: wb.lastSeen,
      })),
    };
  } catch (error) {
    logger.error("[PowerPoint] Failed to get workbook status", error);
    return { total: 0, workbooks: [], error: error.message };
  }
}

Office.actions.associate("openExcelClick", ExcelOpenButtonClick);

// Function to clear all workbooks
export function clearAllWorkbooks(): void {
  try {
    localStorage.removeItem("connectedExcelWorkbooks");
    logger.info("[PowerPoint] All workbooks cleared");
  } catch (error) {
    logger.error("[PowerPoint] Failed to clear all workbooks", error);
  }
}

// Function to remove a specific workbook
export function removeWorkbook(workbookGuid: string): boolean {
  try {
    const existingRaw = localStorage.getItem("connectedExcelWorkbooks");
    if (existingRaw) {
      let existingWorkbooks = JSON.parse(existingRaw);
      if (Array.isArray(existingWorkbooks)) {
        const beforeCount = existingWorkbooks.length;
        existingWorkbooks = existingWorkbooks.filter((wb: any) => wb.workbookGuid !== workbookGuid);

        if (existingWorkbooks.length < beforeCount) {
          localStorage.setItem("connectedExcelWorkbooks", JSON.stringify(existingWorkbooks));
          logger.info(`[PowerPoint] Workbook ${workbookGuid} removed. Remaining: ${existingWorkbooks.length}`);
          return true;
        }
      }
    }
    return false;
  } catch (error) {
    logger.error("[PowerPoint] Failed to remove workbook", error);
    return false;
  }
}

// Function to check if a specific workbook-link combination is refreshable
export function isWorkbookLinkRefreshable(workbookGuid: string, linkId: string): boolean {
  try {
    const connectedRaw = localStorage.getItem("connectedExcelWorkbooks");
    if (!connectedRaw) return false;

    const connectedWorkbooks = JSON.parse(connectedRaw) as Array<{ workbookGuid?: string; linkIds?: string[] }>;
    if (!Array.isArray(connectedWorkbooks)) return false;

    const matchingWorkbook = connectedWorkbooks.find(
      (wb) => wb.workbookGuid === workbookGuid && Array.isArray(wb.linkIds) && wb.linkIds.includes(linkId)
    );

    if (matchingWorkbook) {
      // Update last seen timestamp
      try {
        const updatedWorkbooks = connectedWorkbooks.map((wb) =>
          wb.workbookGuid === workbookGuid ? { ...wb, lastSeen: new Date().toISOString() } : wb
        );
        localStorage.setItem("connectedExcelWorkbooks", JSON.stringify(updatedWorkbooks));
      } catch (_) {}
    }

    return !!matchingWorkbook;
  } catch (error) {
    logger.error("[PowerPoint] Failed to check workbook link refreshability", error);
    return false;
  }
}


// export function contentManagerRefresh( placeholder?: any) {
//   try {
//     if (!placeholder) {
//       logger.warn("[ContentManager] No placeholder payload received from dialog");
//       const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Selected chart is not present in the slide."};
//       localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
//       return;
//     }

//     const { slideId, shapeId } = placeholder || {};
//     if (!slideId || !shapeId) {
//       logger.warn("[ContentManager] Placeholder missing slideId/shapeId", { slideId, shapeId });
//       const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Selected chart is not present in the slide."};
//       localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
//       return;
//     }

//     PowerPoint.run(async (context) => {
//       // Locate slide by id
//       const slides = context.presentation.slides;
//       slides.load("items/id");
//       await context.sync();

//       const slide = slides.items.find((s: any) => s.id === slideId);
//       if (!slide) {
//         logger.warn("[ContentManager] Slide not found for placeholder", { slideId });
//         const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Selected chart is not present in the slide."};
//         localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
//         return;
//       }

//       // Load shapes for the slide and find target shape
//       const shapes = slide.shapes;
//       shapes.load("items");
//       await context.sync();

//       shapes.items.forEach((s: any) => s.load(["id", "name", "tags"]));
//       await context.sync();

//       const targetShape = shapes.items.find((s: any) => String(s.id) === String(shapeId));
//       if (!targetShape) {
//         logger.warn("[ContentManager] Target shape not found on slide", { shapeId, slideId });
//         const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Selected chart is not present in the slide."};
//         localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
//         return;
//       }

//       const tags = (targetShape as any).tags;
//       if (!tags) {
//         logger.warn("[ContentManager] Target shape has no tags collection", { shapeId });
//         const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Selected chart is not present in the slide."};
//         localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
//         return;
//       }

//       tags.load("items");
//       await context.sync();

//       for (const t of (tags.items as any[]) || []) {
//         (t as any).load(["key", "value"]);
//       }
//       await context.sync();

//       const tagMap: Record<string, string> = {};
//       for (const t of (tags.items as any[]) || []) {
//         const k = (t as any)?.key as string;
//         const v = (t as any)?.value as string;
//         if (k && v) tagMap[k.toUpperCase()] = v;
//       }

//       if (!tagMap["WORKBOOKGUID"] || !tagMap["LINKID"]) {
//         logger.warn("[ContentManager] Required tags not present on shape", {
//           hasWorkbookGuid: !!tagMap["WORKBOOKGUID"],
//           hasLinkId: !!tagMap["LINKID"],
//           tagMap,
//         });
//         const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Selected chart is not present in the slide."};
//         localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
//         return;
//       }

//       // Validate workbook presence in localStorage cache
//       const connectedRaw = localStorage.getItem("connectedExcelWorkbooks");
//       if (!connectedRaw) {
//         logger.warn("[ContentManager] No connectedExcelWorkbooks found in localStorage");
//         const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "No connected Excel workbook found for the selected chart."};
//         localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
//         return;
//       }
//       let connected: Array<{ workbookGuid?: string; linkIds?: string[] }> = [];
//       try {
//         connected = JSON.parse(connectedRaw || "[]");
//       } catch (e) {
//         logger.warn("[ContentManager] Failed to parse connectedExcelWorkbooks", e as any);
//         connected = [];
//         const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "No connected Excel workbook found for the selected chart."};
//         localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
//         return;
//       }

//       if (!Array.isArray(connected)) {
//         logger.warn("[ContentManager] connectedExcelWorkbooks is not an array");
//         connected = [];
//         const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "No connected Excel workbook found for the selected chart."};
//         localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
//         return;
//       }

//       const workbookGuid = tagMap["WORKBOOKGUID"];
//       const linkId = tagMap["LINKID"];
//       const match = connected.find(
//         (wb) => wb && wb.workbookGuid === workbookGuid && Array.isArray(wb.linkIds) && wb.linkIds.includes(linkId)
//       );


//       if (match) {
//         logger.warn("[ContentManager] Matching workbook/link found in local cache", { workbookGuid, linkId });
//         const payload = { type: "REFRESH_CHART_STARTED", message: "Refreshing the chart."};
//         localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));

//         // TODO: Refresh the chart
//         return;
//       } else {
//         logger.warn("[ContentManager] Matching workbook/link not found in local cache", { workbookGuid, linkId });
//           const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "No connected Excel workbook found for the selected chart."};
//           localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
//         return;
//       }
//     });
//   } catch (error) {
//     logger.error("[PowerPoint] Failed to content manager refresh", error);
//   }
// }

export async function contentManagerQuickRefresh(placeholder?: any) {

  try {
    if (!placeholder) {
      const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Selected chart is not present in the slide." };
      localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
      return;
    }

    const socket: any = (window as any).__pptSocket;
    if (!socket || socket.disconnected) {
      const payload = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Not connected to the refresh service." };
      localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(payload));
      return;
    }

    await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();

      const slide = slides.items.find((s: any) => s.id === placeholder.slideId);
      if (!slide) {
        const p = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Selected slide not found." };
        localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(p));
        return;
      }


      const shapes = slide.shapes;
      shapes.load("items");
      await context.sync();

      shapes.items.forEach((s: any) => s.load(["id", "name", "tags"]));
      await context.sync();

      const targetShape = shapes.items.find((s: any) => String(s.id) === String(placeholder.shapeId));
      if (!targetShape) {
        console.log("targetShape not found");
        const p = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Selected chart is not present in the slide." };
        localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(p));
        return;
      }

      const tags = (targetShape as any).tags;
      if (!tags) {
        const p = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Selected chart has no tags." };
        localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(p));
        return;
      }

      tags.load("items");
      await context.sync();
      for (const t of (tags.items as any[]) || []) (t as any).load(["key", "value"]);
      await context.sync();

      const tagMap: Record<string, string> = {};
      for (const t of (tags.items as any[]) || []) {
        const k = (t as any)?.key as string;
        const v = (t as any)?.value as string;
        if (k && v) tagMap[k.toUpperCase()] = v;
      }

      const workbookGuid = tagMap["WORKBOOKGUID"];
      const linkId = tagMap["LINKID"];
      if (!workbookGuid || !linkId) {
        const p = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Required chart tags not found (WORKBOOKGUID/LINKID)." };
        localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(p));
        return;
      }

      // Validate workbook presence in local cache
      let connected: Array<{ workbookGuid?: string; linkIds?: string[] }> = [];
      try {
        connected = JSON.parse(localStorage.getItem("connectedExcelWorkbooks") || "[]");
      } catch {
        connected = [];
      }
      const match = Array.isArray(connected)
        ? connected.find((wb) => wb && wb.workbookGuid === workbookGuid && Array.isArray(wb.linkIds) && wb.linkIds.includes(linkId))
        : undefined;

      if (!match) {
        const p = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "No connected Excel workbook found for the selected chart." };
        localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(p));
        return;
      }

      const userId = await getUserId?.();
      const payload = {
        workbookGuid,
        linkId,
        slideShape: { id: targetShape.id, name: (targetShape as any).name },
        requestedAt: new Date().toISOString(),
        userId,
      };
      try {
        (window as any).__pptSocket.emit("pptRefreshRequest", payload);
        localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify({ type: "REFRESH_CHART_STARTED", message: "Refreshing the chart." }));
        localStorage.setItem("placeholderChartRefreshCM", JSON.stringify(placeholder));
      } catch (e) {
        const p = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Failed to emit chart refresh request." };
        localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(p));
      }
    });
  } catch (e) {
    const p = { type: "CONTENT_MANAGER_WORKBOOK_NOT_FOUND", message: "Unexpected error during chart refresh." };
    try { localStorage.setItem("CM_WORKBOOK_NOT_FOUND", JSON.stringify(p)); } catch {}
  }
}